import React, { useEffect } from 'react';
import { CtaPerformanceRow } from '../../../../services/performanceMetricsService';

type Props = {
  cta: CtaPerformanceRow;
  onClose: () => void;
};

const fmtCtr = (clicks: number, views: number): string =>
  views > 0 ? `${((clicks / views) * 100).toFixed(1)}%` : '—';

function StatCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="border border-gray-200 rounded-lg px-3 py-2 bg-white">
      <div className="text-[10px] text-gray-400 uppercase tracking-wider mb-0.5">{label}</div>
      <div className="text-lg font-bold text-gray-900 tabular-nums">{value}</div>
      {sub && <div className="text-[10px] text-gray-400">{sub}</div>}
    </div>
  );
}

export const CtaDetailPanel: React.FC<Props> = ({ cta, onClose }) => {
  // Escape key to close
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/20 z-40" onClick={onClose} />

      {/* Panel */}
      <div className="fixed top-0 right-0 h-full w-[560px] max-w-full bg-white z-50 shadow-2xl border-l border-gray-200 overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-900 leading-tight">
              {cta.headline || cta.id}
            </h2>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <span className="text-xs font-mono text-gray-500">{cta.id}</span>
              {cta.audience && (
                <span className="px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 text-[10px] font-medium uppercase">{cta.audience}</span>
              )}
              {cta.funnel_stages.length > 0 && (
                <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-[10px] font-medium">{cta.funnel_stages.join(' + ')}</span>
              )}
              {!cta.in_registry && (
                <span className="px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[10px] font-medium" title="This CTA id appears on articles or in GA4 events but has no entry in the CTA registry.">
                  not in registry
                </span>
              )}
            </div>
            {cta.target_url && (
              <a
                href={cta.target_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-blue-600 hover:text-blue-800 break-all mt-1 block"
              >
                {cta.target_url}
              </a>
            )}
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none shrink-0">
            ×
          </button>
        </div>

        <div className="px-6 py-4">
          {/* Stats */}
          <div className="grid grid-cols-5 gap-2 mb-5">
            <StatCard label="Articles" value={String(cta.articles_count)} />
            <StatCard label="Slots" value={String(cta.placements_count)} sub="CTA blocks" />
            <StatCard label="Views" value={String(cta.views)} />
            <StatCard label="Clicks" value={String(cta.clicks)} sub={cta.clicks !== cta.clicks_recent ? `${cta.clicks_recent} since views` : 'all-time'} />
            <StatCard label="CTR" value={fmtCtr(cta.clicks_recent, cta.views)} sub={cta.views === 0 ? 'needs views' : 'same window'} />
          </div>

          {/* Per-article breakdown */}
          <div className="text-xs text-gray-500 uppercase tracking-wider mb-2">
            Articles ({cta.articles.length})
          </div>
          {cta.articles.length === 0 ? (
            <div className="text-sm text-gray-400">Not placed on any published article.</div>
          ) : (
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-[10px] text-gray-400 uppercase tracking-wider">
                  <th className="text-left py-1.5 font-medium">Article</th>
                  <th className="text-right py-1.5 font-medium w-14" title="Number of times this CTA block appears in the article">Slots</th>
                  <th className="text-right py-1.5 font-medium w-14">Views</th>
                  <th className="text-right py-1.5 font-medium w-14">Clicks</th>
                  <th className="text-right py-1.5 font-medium w-14">CTR</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cta.articles.map((a) => (
                  <tr key={a.url}>
                    <td className="py-2 pr-2">
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-800 hover:text-blue-700 line-clamp-2"
                        dangerouslySetInnerHTML={{ __html: a.title }}
                      />
                      {a.placements === 0 && (
                        <span className="text-[10px] text-amber-600" title="GA4 recorded activity for this CTA on this article, but the CTA no longer appears in the article content.">
                          no longer placed
                        </span>
                      )}
                    </td>
                    <td className="py-2 text-right tabular-nums text-gray-600">{a.placements || '—'}</td>
                    <td className="py-2 text-right tabular-nums text-gray-600">{a.views}</td>
                    <td className="py-2 text-right tabular-nums font-medium text-gray-900" title={`${a.clicks} all-time · ${a.clicks_recent} since view tracking`}>{a.clicks}</td>
                    <td className="py-2 text-right tabular-nums text-gray-600" title={`${a.clicks_recent} same-window clicks / ${a.views} views`}>{fmtCtr(a.clicks_recent, a.views)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </>
  );
};
